/**
 * The browser's own database for what the app keeps with no signal (unit 25B,
 * D10): the cache of rows, the outbox of writes, the failures to show, and two
 * facts — whose they are, and the change-log cursor.
 *
 * One database, opened by the page and by the Service Worker alike. A reload
 * — the one a new Service Worker causes (v1.84) — loses nothing: nothing here
 * lives in memory.
 */

export const DB_NAME = "kindred-offline";
const VERSION = 1;

export type Store = "outbox" | "failures" | "cache" | "meta";

export function openOffline(name: string = DB_NAME): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      db.createObjectStore("outbox", { keyPath: "seq", autoIncrement: true });
      db.createObjectStore("failures", { keyPath: "seq", autoIncrement: true });
      db.createObjectStore("cache", { keyPath: ["entity", "id"] });
      db.createObjectStore("meta");
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("could not open the offline store"));
  });
}

/**
 * Run `body` in one transaction, and settle when it commits — with what
 * `body` put in `result`, or the error that aborted it. IndexedDB commits a
 * transaction when no request is pending, so `body` issues its requests in
 * callbacks and never awaits anything else.
 */
export function transact<T>(
  db: IDBDatabase,
  stores: Store[],
  mode: IDBTransactionMode,
  body: (t: IDBTransaction, result: { value?: T }) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(stores, mode);
    const result: { value?: T } = {};
    t.oncomplete = () => resolve(result.value as T);
    t.onerror = () => reject(t.error ?? new Error("the offline store refused a write"));
    t.onabort = () => reject(t.error ?? new Error("aborted"));
    try {
      body(t, result);
    } catch (error) {
      t.abort();
      reject(error);
    }
  });
}

/** Every value in a store, in key order. */
export function all<T>(db: IDBDatabase, store: Store): Promise<T[]> {
  return transact<T[]>(db, [store], "readonly", (t, result) => {
    const request = t.objectStore(store).getAll();
    request.onsuccess = () => {
      result.value = request.result as T[];
    };
  });
}

export function getMeta<T>(db: IDBDatabase, key: string): Promise<T | undefined> {
  return transact<T | undefined>(db, ["meta"], "readonly", (t, result) => {
    const request = t.objectStore("meta").get(key);
    request.onsuccess = () => {
      result.value = request.result as T | undefined;
    };
  });
}
